import { cn } from '@/lib/cn'
import { ChevronDown, Search } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'

export interface ComboboxOption {
  value: string
  label: string
  /** A second line: the detail that tells two options apart. */
  hint?: string
}

export interface ComboboxProps {
  label?: ReactNode
  /** Accessible name when the visible label is rendered outside the combobox. */
  ariaLabel?: string
  value: string | null
  /** What the trigger shows for the chosen value, when it is not among `options`. */
  selectedLabel?: string
  options: ComboboxOption[]
  onChange: (value: string | null) => void
  /**
   * Called with the search term. Leave it out and the options are filtered here
   * instead; pass it when the list is too large to hold in the browser.
   */
  onSearch?: (term: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  loading?: boolean
}

/**
 * A picker for one value out of many, with a search box.
 *
 * A native select stops being usable once there are hundreds of options: it
 * cannot be typed into and it cannot be searched. This one can, and it says so
 * with the same listbox roles the browser uses.
 */
export function Combobox({
  label,
  ariaLabel,
  value,
  selectedLabel,
  options,
  onChange,
  onSearch,
  placeholder = 'Choose…',
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing found',
  loading = false,
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const container = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const listId = useId()
  const labelId = useId()
  const valueId = useId()

  const visible = useMemo(() => {
    if (onSearch) return options
    const needle = term.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => option.label.toLowerCase().includes(needle))
  }, [options, onSearch, term])

  const active = visible[Math.min(highlighted, visible.length - 1)]

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function toggle() {
    if (open) {
      close()
      return
    }
    setOpen(true)
    setTerm('')
    setHighlighted(0)
    onSearch?.('')
  }

  function close() {
    setOpen(false)
    // The search box goes away with the panel, so the focus goes back to the
    // button that opened it.
    trigger.current?.focus()
  }

  function choose(option: ComboboxOption) {
    onChange(option.value)
    close()
    setTerm('')
  }

  return (
    <div ref={container} className="relative flex min-w-0 flex-col gap-1.5">
      {label ? (
        <span id={labelId} className="text-label font-medium text-ink-soft">
          {label}
        </span>
      ) : null}
      <button
        ref={trigger}
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={!label ? ariaLabel : undefined}
        aria-labelledby={label ? `${labelId} ${valueId}` : ariaLabel ? undefined : valueId}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-[var(--radius-field)] border border-line-strong bg-surface px-3 text-sm text-ink hover:border-ink-faint focus:border-accent"
      >
        <span id={valueId} className={cn('truncate', !value && 'text-ink-faint')}>
          {value ? (selectedLabel ?? value) : placeholder}
        </span>
        <ChevronDown size={14} aria-hidden className="shrink-0 text-ink-faint" />
      </button>

      {open ? (
        <div className="absolute top-full left-0 z-20 mt-1 w-full min-w-64 rounded-[var(--radius-field)] border border-line bg-surface p-1.5 shadow-raised">
          <div className="relative">
            <Search
              size={14}
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-faint"
            />
            <input
              autoFocus
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-label={
                typeof label === 'string'
                  ? `${label}: search`
                  : ariaLabel
                    ? `${ariaLabel}: search`
                    : 'Search'
              }
              placeholder={searchPlaceholder}
              value={term}
              onChange={(event) => {
                setTerm(event.target.value)
                setHighlighted(0)
                onSearch?.(event.target.value)
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault()
                  setHighlighted((current) => Math.min(current + 1, visible.length - 1))
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault()
                  setHighlighted((current) => Math.max(current - 1, 0))
                } else if (event.key === 'Enter') {
                  event.preventDefault()
                  if (active) choose(active)
                } else if (event.key === 'Escape') {
                  close()
                }
              }}
              className="h-9 w-full rounded-[var(--radius-field)] border border-line-strong bg-surface pr-2 pl-8 text-sm text-ink placeholder:text-ink-faint/70 hover:border-ink-faint focus:border-accent"
            />
          </div>

          <ul
            id={listId}
            role="listbox"
            className="scroll-slim mt-1 max-h-72 overflow-y-auto"
          >
            {visible.length === 0 ? (
              <li className="px-2 py-3 text-center text-xs text-ink-faint">
                {loading ? 'Searching…' : emptyText}
              </li>
            ) : (
              visible.map((option, index) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => choose(option)}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left text-sm',
                      index === highlighted
                        ? 'bg-accent-soft text-accent-ink'
                        : 'text-ink hover:bg-surface-sunken',
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.hint ? (
                      <span className="truncate text-xs text-ink-faint">{option.hint}</span>
                    ) : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
